import React, { useEffect, useState, useCallback } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { api, formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import SEO from "../components/SEO";
import {
  LogOut, Plus, Trash2, Pencil, Save, X, Calendar, Newspaper, FileText,
  Inbox, RefreshCw, ChevronRight, ExternalLink,
} from "lucide-react";

const TABS = [
  { key: "quote", label: "Quote Inquiries", icon: FileText },
  { key: "contact", label: "Contact Messages", icon: Inbox },
  { key: "supplier", label: "Supplier Applications", icon: Inbox },
  { key: "articles", label: "News & Insights", icon: Newspaper },
];

function StatusBadge({ status }) {
  const map = { new: ["#dcfce7", "#166534"], "in-progress": ["#fef3c7", "#92400e"], closed: ["#e5e7eb", "#374151"] };
  const [bg, color] = map[status] || map.new;
  return <span className="px-2 py-0.5 rounded-full text-[11px] font-semibold uppercase tracking-wider" style={{ background: bg, color }}>{status}</span>;
}

function InquiryList({ kind }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/inquiries`, { params: { kind } });
      setItems(data.items || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [kind]);

  useEffect(() => { load(); }, [load]);

  const changeStatus = async (id, status) => {
    try {
      await api.post(`/admin/inquiries/${id}/status`, null, { params: { status } });
      toast.success("Status updated");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  if (loading) return <p className="text-muted-foreground">Loading…</p>;
  if (!items.length) return <p className="text-muted-foreground">No submissions yet.</p>;

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">{items.length} submission{items.length > 1 ? "s" : ""}</p>
        <Button variant="outline" size="sm" onClick={load} data-testid="refresh-inquiries"><RefreshCw size={14} className="mr-2" />Refresh</Button>
      </div>
      {items.map((it) => (
        <div key={it.id} className="rounded-xl border border-border bg-white shadow-sm" data-testid={`inquiry-${it.id}`}>
          <button onClick={() => setExpanded(expanded === it.id ? null : it.id)} className="w-full px-5 py-4 flex items-center justify-between hover:bg-muted/30 transition text-left">
            <div className="min-w-0">
              <div className="flex items-center gap-3">
                <span className="font-semibold text-foreground">{it.name || it.contact_name || it.company_name}</span>
                <StatusBadge status={it.status || "new"} />
              </div>
              <div className="text-xs text-muted-foreground mt-1 truncate">
                {it.email} · {new Date(it.created_at).toLocaleString()}
                {it.company && ` · ${it.company}`}
                {it.product && ` · ${it.product}`}
                {it.subject && ` · ${it.subject}`}
              </div>
            </div>
            <ChevronRight size={16} className={`text-muted-foreground transition-transform ${expanded === it.id ? "rotate-90" : ""}`} />
          </button>
          {expanded === it.id && (
            <div className="px-5 pb-5 border-t border-border">
              <dl className="grid md:grid-cols-2 gap-x-6 gap-y-2 text-sm mt-4">
                {Object.entries(it).filter(([k]) => !["id", "kind", "_id"].includes(k)).map(([k, v]) => (
                  <div key={k} className="flex gap-2">
                    <dt className="text-muted-foreground min-w-[110px] capitalize">{k.replace(/_/g, " ")}:</dt>
                    <dd className="font-medium text-foreground break-words">{Array.isArray(v) ? v.join(", ") : String(v ?? "—")}</dd>
                  </div>
                ))}
              </dl>
              <div className="mt-4 flex gap-2">
                {["new", "in-progress", "closed"].map((s) => (
                  <Button key={s} variant={it.status === s ? "default" : "outline"} size="sm" onClick={() => changeStatus(it.id, s)} data-testid={`status-${s}-${it.id}`}>
                    Mark {s}
                  </Button>
                ))}
              </div>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

function ArticleForm({ initial, onCancel, onSave }) {
  const [form, setForm] = useState(initial || {
    title: "", type: "news", excerpt: "", body: "", image: "", tags: "", published: true,
    event_date: "", event_location: "", event_url: "",
  });
  const [busy, setBusy] = useState(false);

  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    const payload = {
      ...form,
      tags: typeof form.tags === "string" ? form.tags.split(",").map(t => t.trim()).filter(Boolean) : (form.tags || []),
    };
    if (!payload.event_date) delete payload.event_date;
    try {
      if (initial?.id) {
        const { data } = await api.put(`/admin/articles/${initial.id}`, payload);
        onSave(data, "updated");
      } else {
        const { data } = await api.post(`/admin/articles`, payload);
        onSave(data, "created");
      }
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };

  return (
    <form onSubmit={submit} className="bg-white border border-border rounded-2xl p-6 space-y-4" data-testid="article-form">
      <div className="flex items-center justify-between">
        <h3 className="font-display text-xl font-semibold" style={{ color: "var(--brand-700)" }}>{initial?.id ? "Edit Article" : "New Article"}</h3>
        <Button type="button" variant="ghost" size="sm" onClick={onCancel} data-testid="cancel-article-form"><X size={16} /></Button>
      </div>
      <div className="grid md:grid-cols-3 gap-4">
        <div className="md:col-span-2">
          <Label>Title *</Label>
          <Input value={form.title} onChange={(e) => upd("title", e.target.value)} required data-testid="article-title" />
        </div>
        <div>
          <Label>Type *</Label>
          <select className="flex h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm" value={form.type} onChange={(e) => upd("type", e.target.value)} data-testid="article-type">
            <option value="news">News</option>
            <option value="event">Event</option>
          </select>
        </div>
      </div>
      <div>
        <Label>Excerpt</Label>
        <Input value={form.excerpt || ""} onChange={(e) => upd("excerpt", e.target.value)} placeholder="One-liner shown on card" />
      </div>
      <div>
        <Label>Body (Markdown/HTML) *</Label>
        <Textarea rows={7} value={form.body} onChange={(e) => upd("body", e.target.value)} required data-testid="article-body" />
      </div>
      <div className="grid md:grid-cols-2 gap-4">
        <div><Label>Image URL</Label><Input value={form.image || ""} onChange={(e) => upd("image", e.target.value)} placeholder="/images/... or https://..." /></div>
        <div><Label>Tags (comma-separated)</Label><Input value={typeof form.tags === "string" ? form.tags : (form.tags || []).join(", ")} onChange={(e) => upd("tags", e.target.value)} placeholder="cement, logistics" /></div>
      </div>
      {form.type === "event" && (
        <div className="rounded-xl border border-border p-4 grid md:grid-cols-3 gap-4" style={{ background: "var(--brand-50)" }}>
          <div><Label>Event Date</Label><Input type="datetime-local" value={form.event_date ? form.event_date.slice(0,16) : ""} onChange={(e) => upd("event_date", e.target.value ? new Date(e.target.value).toISOString() : "")} data-testid="event-date" /></div>
          <div><Label>Location</Label><Input value={form.event_location || ""} onChange={(e) => upd("event_location", e.target.value)} placeholder="Dubai, UAE" /></div>
          <div><Label>Event URL</Label><Input value={form.event_url || ""} onChange={(e) => upd("event_url", e.target.value)} placeholder="https://..." /></div>
        </div>
      )}
      <div className="flex items-center gap-3 pt-2">
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={form.published} onChange={(e) => upd("published", e.target.checked)} data-testid="article-published" />
          Published (visible on website)
        </label>
      </div>
      <div className="flex gap-3 pt-3">
        <Button type="submit" disabled={busy} className="text-white" style={{ backgroundColor: "var(--brand-700)" }} data-testid="save-article">
          <Save size={14} className="mr-2" />{busy ? "Saving…" : "Save Article"}
        </Button>
        <Button type="button" variant="outline" onClick={onCancel}>Cancel</Button>
      </div>
    </form>
  );
}

function ArticleList() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null=none, object=editing/new

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/articles");
      setItems(data.items || []);
    } catch (e) { toast.error(formatApiError(e)); }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const del = async (id) => {
    if (!window.confirm("Delete this article?")) return;
    try { await api.delete(`/admin/articles/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  if (editing !== null) {
    return <ArticleForm initial={editing} onCancel={() => setEditing(null)} onSave={(_, action) => { toast.success(`Article ${action}`); setEditing(null); load(); }} />;
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">{items.length} article{items.length !== 1 && "s"}</p>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={load}><RefreshCw size={14} className="mr-2" />Refresh</Button>
          <Button size="sm" onClick={() => setEditing({})} className="text-white" style={{ backgroundColor: "var(--brand-700)" }} data-testid="new-article-btn"><Plus size={14} className="mr-2" />New Article</Button>
        </div>
      </div>
      {loading ? <p className="text-muted-foreground">Loading…</p> : (
        <div className="rounded-xl border border-border bg-white overflow-hidden">
          <table className="w-full text-sm">
            <thead className="text-left text-[11px] uppercase tracking-wider text-muted-foreground border-b border-border" style={{ background: "var(--brand-50)" }}>
              <tr>
                <th className="px-5 py-3">Title</th>
                <th className="px-5 py-3">Type</th>
                <th className="px-5 py-3">Status</th>
                <th className="px-5 py-3">Published</th>
                <th className="px-5 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 ? (
                <tr><td colSpan={5} className="px-5 py-8 text-center text-muted-foreground">No articles yet. Click <b>New Article</b> to create one.</td></tr>
              ) : items.map((a) => (
                <tr key={a.id} className="border-b border-border last:border-0 hover:bg-muted/30" data-testid={`article-row-${a.id}`}>
                  <td className="px-5 py-3">
                    <div className="font-semibold text-foreground">{a.title}</div>
                    <div className="text-xs text-muted-foreground">/{a.slug}</div>
                  </td>
                  <td className="px-5 py-3">
                    <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[11px] font-semibold uppercase tracking-wider" style={{ background: a.type === "event" ? "#fef3c7" : "var(--brand-100)", color: a.type === "event" ? "#92400e" : "var(--brand-700)" }}>
                      {a.type === "event" ? <Calendar size={11} /> : <Newspaper size={11} />}{a.type}
                    </span>
                  </td>
                  <td className="px-5 py-3">
                    <span className={`text-xs font-semibold ${a.published ? "text-green-700" : "text-muted-foreground"}`}>{a.published ? "Published" : "Draft"}</span>
                  </td>
                  <td className="px-5 py-3 text-xs text-muted-foreground">{new Date(a.published_at || a.created_at).toLocaleDateString()}</td>
                  <td className="px-5 py-3 text-right">
                    <div className="flex gap-2 justify-end">
                      {a.published && <a href={`/news/${a.slug}`} target="_blank" rel="noreferrer" className="p-2 hover:bg-muted rounded-md" title="View"><ExternalLink size={14} /></a>}
                      <button onClick={() => setEditing(a)} className="p-2 hover:bg-muted rounded-md" title="Edit" data-testid={`edit-article-${a.id}`}><Pencil size={14} /></button>
                      <button onClick={() => del(a.id)} className="p-2 hover:bg-red-50 text-red-600 rounded-md" title="Delete" data-testid={`delete-article-${a.id}`}><Trash2 size={14} /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default function Admin() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("quote");

  if (user === null) return <div className="min-h-screen flex items-center justify-center text-muted-foreground">Loading…</div>;
  if (!user || user.role !== "admin") return <Navigate to="/admin/login" replace />;

  return (
    <>
      <SEO title="Admin Dashboard" description="Ruken Alazdehar admin dashboard." path="/admin" noindex />
      <section className="min-h-screen" style={{ background: "linear-gradient(180deg, var(--brand-50), #fff 30%)" }}>
        <div className="border-b border-border bg-white sticky top-0 z-40">
          <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
            <div className="flex items-center gap-4">
              <img src="/logo.png" alt="Ruken" className="h-9 w-auto object-contain bg-white rounded p-1" />
              <div>
                <p className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">Admin Dashboard</p>
                <h1 className="font-display text-lg font-bold leading-none" style={{ color: "var(--brand-700)" }}>Ruken CMS</h1>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <p className="text-xs text-muted-foreground hidden sm:block">Signed in as <span className="font-semibold text-foreground">{user.email}</span></p>
              <Button variant="outline" size="sm" onClick={async () => { await logout(); navigate("/admin/login"); }} data-testid="admin-logout"><LogOut size={14} className="mr-2" />Sign Out</Button>
            </div>
          </div>
        </div>
        <div className="max-w-7xl mx-auto px-6 py-8">
          <div className="flex flex-wrap gap-2 mb-8">
            {TABS.map(({ key, label, icon: Ico }) => (
              <button key={key} onClick={() => setTab(key)} data-testid={`admin-tab-${key}`}
                className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold border transition ${
                  tab === key ? "text-white" : "hover:border-brand text-foreground/70"
                }`}
                style={tab === key
                  ? { backgroundColor: "var(--brand-700)", borderColor: "var(--brand-700)" }
                  : { borderColor: "var(--border)" }}
              >
                <Ico size={14} /> {label}
              </button>
            ))}
          </div>
          {tab === "articles" ? <ArticleList /> : <InquiryList kind={tab} />}
        </div>
      </section>
    </>
  );
}
